import {
  CHANGE_IMAGE_DRAWER_TAB,
  ChangeImageDrawerTabAction,
  SHOW_IMAGE_GENERATION_VIEW,
  ShowImageGenerationViewAction,
  GET_CATEGORY_IMAGES,
  GetCategoryImagesAction,
  GET_CATEGORY_IMAGES_SUCCESS,
  GetCategoryImagesSuccessAction,
  GET_CATEGORY_IMAGES_FAILURE,
  GetCategoryImagesFailureAction,
  GET_SEARCH_IMAGES,
  GetSearchImagesAction,
  GET_SEARCH_IMAGES_SUCCESS,
  GetSearchImagesSuccessAction,
  GET_SEARCH_IMAGES_FAILURE,
  GetSearchImagesFailureAction,
  UPDATE_SEARCH_TAB_VIEWED,
  UpdateSearchTabViewedAction
} from 'js/actionCreators/ImageDrawerActions';
import {
  CLEAR_ALL_FILTERS,
  ClearAllFiltersAction,
  RESET_SEARCH,
  ResetSearchAction,
  SET_SELECTED_CATEGORY,
  SetSelectedCategoryAction,
  TOGGLE_SEARCH_FILTER,
  ToggleSearchFilterAction,
  ACTIVATE_VISUAL_STYLE_SEARCH,
  ActivateVisualStyleSearchAction,
  SELECT_CHARACTER,
  SelectCharacterAction
} from 'js/actionCreators/imageSearchActions';
import { EDITOR_CLOSED, EditorClosedAction } from 'js/actionCreators/editorActions';
import {
  SHOW_LEFT_HAND_PANEL,
  ShowLeftHandPanelAction,
  CLOSE_LEFT_HAND_PANEL,
  CloseLeftHandPanelAction
} from 'js/actionCreators/uiActions';
import { ImageDrawerState, ImageDrawerTab } from 'js/types';

type ImageDrawerActionTypes =
  | ChangeImageDrawerTabAction
  | ShowImageGenerationViewAction
  | ShowLeftHandPanelAction
  | CloseLeftHandPanelAction
  | GetCategoryImagesAction
  | GetCategoryImagesSuccessAction
  | GetCategoryImagesFailureAction
  | GetSearchImagesAction
  | GetSearchImagesSuccessAction
  | GetSearchImagesFailureAction
  | UpdateSearchTabViewedAction
  | SetSelectedCategoryAction
  | ToggleSearchFilterAction
  | ClearAllFiltersAction
  | ActivateVisualStyleSearchAction
  | SelectCharacterAction
  | EditorClosedAction
  | ResetSearchAction;

const initialState: ImageDrawerState = {
  panelTab: ImageDrawerTab.IMAGE_LIBRARY,
  view: 'library',
  imageLibrary: {
    category: {
      videoscribe: {},
      'noun-project': {},
      'streamline-graphics': {}
    },
    search: {
      videoscribe: {},
      'noun-project': {},
      'streamline-graphics': {}
    }
  }
};

export default function imageDrawerReducer(state = initialState, action: ImageDrawerActionTypes): ImageDrawerState {
  switch (action.type) {
    case RESET_SEARCH:
    case EDITOR_CLOSED: {
      return {
        ...state,
        panelTab: ImageDrawerTab.IMAGE_LIBRARY,
        view: 'library',
        imageLibrary: {
          category: {
            videoscribe: {},
            'noun-project': {},
            'streamline-graphics': {}
          },
          search: {
            videoscribe: {},
            'noun-project': {},
            'streamline-graphics': {}
          }
        }
      };
    }

    case CHANGE_IMAGE_DRAWER_TAB: {
      return {
        ...state,
        panelTab: action.tab,
        view: 'library'
      };
    }

    case SHOW_IMAGE_GENERATION_VIEW: {
      return { ...state, view: 'imageGeneration' };
    }

    case SHOW_LEFT_HAND_PANEL:
    case CLOSE_LEFT_HAND_PANEL: {
      return state.view === 'imageGeneration' ? { ...state, view: 'library' } : state;
    }

    case SET_SELECTED_CATEGORY: {
      return {
        ...state,
        panelTab: ImageDrawerTab.IMAGE_LIBRARY,
        view: 'library'
      };
    }

    case GET_CATEGORY_IMAGES: {
      const { imageSource, categoryId } = action;
      return {
        ...state,
        imageLibrary: {
          ...state.imageLibrary,
          category: {
            ...state.imageLibrary.category,
            [imageSource]: {
              ...state.imageLibrary.category[imageSource],
              [categoryId]: {
                ...(state.imageLibrary.category[imageSource][categoryId] || {}),
                loading: true
              }
            }
          }
        }
      };
    }

    case GET_CATEGORY_IMAGES_SUCCESS: {
      const { categoryId, imageSource, images, nextPageId, pagination } = action;
      // de-duplicate images in case pagination returns duplicates
      const existingImages = state.imageLibrary.category[imageSource][categoryId]?.images || [];
      const newImages = images.filter(
        newImage => !existingImages.some(existingImage => existingImage.id === newImage.id)
      );
      return {
        ...state,
        imageLibrary: {
          ...state.imageLibrary,
          category: {
            ...state.imageLibrary.category,
            [imageSource]: {
              ...state.imageLibrary.category[imageSource],
              [categoryId]: {
                loading: false,
                images: [...existingImages, ...newImages],
                nextPageId,
                pagination
              }
            }
          }
        }
      };
    }

    case GET_CATEGORY_IMAGES_FAILURE: {
      const { categoryId, imageSource } = action;
      return {
        ...state,
        imageLibrary: {
          ...state.imageLibrary,
          category: {
            ...state.imageLibrary.category,
            [imageSource]: {
              ...state.imageLibrary.category[imageSource],
              [categoryId]: {
                ...(state.imageLibrary.category[imageSource][categoryId] || {}),
                loading: false
              }
            }
          }
        }
      };
    }

    case GET_SEARCH_IMAGES: {
      const { imageSource, searchInput } = action;
      return {
        ...state,
        imageLibrary: {
          ...state.imageLibrary,
          search: {
            ...state.imageLibrary.search,
            [imageSource]: {
              ...state.imageLibrary.search[imageSource],
              [searchInput]: {
                ...(state.imageLibrary.search[imageSource][searchInput] || {}),
                loading: true
              }
            }
          }
        }
      };
    }

    case GET_SEARCH_IMAGES_SUCCESS: {
      const { imageSource, searchInput, images, nextPageId, offset, total, isPrefetch, pagination } = action;
      const existingImages = state.imageLibrary.search[imageSource][searchInput]?.images || [];
      const newImages = images.filter(
        newImage => !existingImages.some(existingImage => existingImage.id === newImage.id)
      );
      return {
        ...state,
        imageLibrary: {
          ...state.imageLibrary,
          search: {
            ...state.imageLibrary.search,
            [imageSource]: {
              ...state.imageLibrary.search[imageSource],
              [searchInput]: {
                loading: false,
                images: [...existingImages, ...newImages],
                nextPageId,
                offset: Math.min(offset, total),
                total,
                isViewed: !isPrefetch,
                pagination
              }
            }
          }
        }
      };
    }

    case GET_SEARCH_IMAGES_FAILURE: {
      const { imageSource, searchInput } = action;
      return {
        ...state,
        imageLibrary: {
          ...state.imageLibrary,
          search: {
            ...state.imageLibrary.search,
            [imageSource]: {
              ...state.imageLibrary.search[imageSource],
              [searchInput]: {
                ...(state.imageLibrary.search[imageSource][searchInput] || {}),
                loading: false
              }
            }
          }
        }
      };
    }

    case UPDATE_SEARCH_TAB_VIEWED: {
      const { imageSource, searchInput, isViewed } = action;
      return {
        ...state,
        imageLibrary: {
          ...state.imageLibrary,
          search: {
            ...state.imageLibrary.search,
            [imageSource]: {
              ...state.imageLibrary.search[imageSource],
              [searchInput]: {
                ...(state.imageLibrary.search[imageSource][searchInput] || {}),
                isViewed
              }
            }
          }
        }
      };
    }

    case SELECT_CHARACTER:
    case CLEAR_ALL_FILTERS:
    case ACTIVATE_VISUAL_STYLE_SEARCH:
    case TOGGLE_SEARCH_FILTER: {
      return {
        ...state,
        imageLibrary: {
          ...state.imageLibrary,
          search: {
            ...state.imageLibrary.search,
            videoscribe: {}
          }
        }
      };
    }

    default:
      return state;
  }
}
